import React from "react";
import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

/**
 * Higher-Order Component to replace the removed withRouter from React Router v5
 * This HOC provides router props (location, navigate, params) to class components
 *
 * Usage:
 * export default withRouter(YourClassComponent);
 *
 * The wrapped component will receive these props:
 * - location: current location object
 * - navigate: function to navigate programmatically (replaces history.push)
 * - params: URL parameters
 * - match: object containing url, path, and params (for compatibility)
 */
function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    // Create a match object similar to React Router v5 for compatibility
    const match = {
      params,
      pathname: location.pathname,
      pathnameBase: location.pathname,
      pattern: {
        path: location.pathname,
        caseSensitive: false,
        end: true,
      },
      // For compatibility with existing code that uses match.url or match.path
      url: location.pathname,
      path: location.pathname,
    };

    // Create a history-like object for compatibility with existing code
    const history = {
      push: (path, state) => navigate(path, { state }),
      replace: (path, state) =>
        navigate(path, { replace: true, state }),
      go: n => navigate(n),
      goBack: () => navigate(-1),
      goForward: () => navigate(1),
      location,
    };

    return (
      // eslint-disable-next-line react/jsx-props-no-spreading
      <Component
        {...props}
        location={location}
        navigate={navigate}
        params={params}
        match={match}
        history={history}
      />
    );
  }

  // Set display name for debugging
  ComponentWithRouterProp.displayName = `withRouter(${Component.displayName || Component.name || "Component"})`;

  return ComponentWithRouterProp;
}

export default withRouter;
